// Classement des supporters Fan Zone
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Trophy, ArrowLeft, Crown, Medal, Award, Users, Loader2 } from 'lucide-react';
import { getLeaderboard, getUserRank, getOrCreateUserProfile } from './services/fanZoneService';
import { LEVELS_CONFIG, calculateLevel } from './services/fanZoneConfig';
import logger from '../../utils/logger';

// Scrollbar styles pour la Fan Zone
const scrollbarStyles = `
  .fanzone-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(255, 228, 0, 0.3) transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar {
    width: 6px;
  }
  .fanzone-scrollbar::-webkit-scrollbar-track {
    background: transparent;
  }
  .fanzone-scrollbar::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 228, 0, 0.4) 0%, rgba(189, 255, 0, 0.2) 100%);
    border-radius: 3px;
  }
`;

const RankBadge = ({ rank }) => {
  if (rank === 1) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-300 to-yellow-600 flex items-center justify-center shadow-lg shadow-yellow-500/30">
        <Crown className="w-5 h-5 text-yellow-900" />
      </div>
    );
  }
  if (rank === 2) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gray-300 to-gray-500 flex items-center justify-center shadow-lg shadow-gray-400/30">
        <Medal className="w-5 h-5 text-gray-700" />
      </div>
    );
  }
  if (rank === 3) {
    return (
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-700 flex items-center justify-center shadow-lg shadow-amber-500/30">
        <Award className="w-5 h-5 text-amber-900" />
      </div>
    );
  }
  
  return (
    <div className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-gray-400 font-bold">
      {rank}
    </div>
  );
};

const LeaderboardRow = ({ player, index, isCurrentUser }) => {
  const levelInfo = player.levelInfo || calculateLevel(player.totalXP || 0);
  const levelColor = levelInfo?.color || LEVELS_CONFIG[0].color;

  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      className={`
        flex items-center gap-4 p-4 rounded-xl transition-all duration-200
        ${isCurrentUser 
          ? 'bg-gradient-to-r from-[#FFE400]/10 to-[#BDFF00]/5 border border-[#FFE400]/30' 
          : 'bg-white/[0.02] hover:bg-white/[0.05] border border-transparent'
        }
      `}
    >
      {/* Rank */}
      <RankBadge rank={player.rank} />
      
      {/* Avatar */}
      <div 
        className="w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold"
        style={{
          background: `linear-gradient(135deg, ${levelColor}40, ${levelColor}20)`,
          border: `2px solid ${levelColor}`,
        }}
      >
        {player.pseudo?.charAt(0)?.toUpperCase() || 'F'}
      </div>
      
      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-bold truncate ${isCurrentUser ? 'text-[#FFE400]' : 'text-white'}`}>
            {player.pseudo}
          </span>
          {isCurrentUser && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFE400]/20 text-[#FFE400]">
              Vous
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <span style={{ color: levelColor }}>{levelInfo?.title}</span>
          <span className="text-gray-500">•</span>
          <span className="text-gray-400">Niv. {levelInfo?.level}</span>
        </div>
      </div>
      
      {/* XP */}
      <div className="text-right">
        <div className="text-lg font-bold text-[#BDFF00]">
          {player.totalXP?.toLocaleString()}
        </div>
        <div className="text-xs text-gray-500">XP</div>
      </div>
    </motion.div>
  );
};

const FanZoneLeaderboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState({ rank: 0, totalPlayers: 0 });
  const [userProfile, setUserProfile] = useState(null);
  const [timeframe, setTimeframe] = useState('all');

  // Injecter les styles de scrollbar
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = scrollbarStyles;
    document.head.appendChild(styleEl);
    document.documentElement.classList.add('fanzone-scrollbar');
    document.body.classList.add('fanzone-scrollbar');
    
    return () => {
      document.head.removeChild(styleEl);
      document.documentElement.classList.remove('fanzone-scrollbar');
      document.body.classList.remove('fanzone-scrollbar');
    };
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Charger le classement
        const data = await getLeaderboard(timeframe, 50);
        setLeaderboard(data);
        
        // Si utilisateur connecté, charger son rang
        if (currentUser) {
          const rank = await getUserRank(currentUser.uid);
          setUserRank(rank);
          
          const profile = await getOrCreateUserProfile(currentUser.uid);
          setUserProfile(profile);
        }
      } catch (error) {
        logger.error('Erreur chargement classement:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentUser, timeframe]);

  return (
    <div 
      className="min-h-screen pb-20"
      style={{ background: 'linear-gradient(135deg, #0a0f1a 0%, #0d1526 25%, #0a1628 50%, #091320 75%, #050a12 100%)' }}
    >
      {/* Decorative elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[#FFE400]/5 blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full bg-[#BDFF00]/5 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-2xl mx-auto px-4 pt-6">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between mb-8"
        >
          <button
            onClick={() => navigate('/fanzone/dashboard')}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Retour</span>
          </button>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-[#FFE400]/20 to-[#BDFF00]/10 mb-4">
            <Trophy className="w-8 h-8 text-[#FFE400]" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Classement</h1>
          <p className="text-gray-400">Les meilleurs supporters EFAFC</p>
        </motion.div>

        {/* User rank card si connecté */}
        {currentUser && userProfile && userRank.rank > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-6 p-4 rounded-2xl"
            style={{
              background: 'linear-gradient(135deg, rgba(255,228,0,0.1) 0%, rgba(189,255,0,0.05) 100%)',
              border: '1px solid rgba(255,228,0,0.2)',
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-5 h-5 text-[#FFE400]" />
                <span className="text-white">Votre position</span>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-[#FFE400]">#{userRank.rank}</div>
                <div className="text-xs text-gray-400">sur {userRank.totalPlayers} joueurs</div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Timeframe tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex gap-2 mb-6"
        >
          {[
            { id: 'all', label: 'Tout temps' },
            { id: 'month', label: 'Ce mois' },
            { id: 'week', label: 'Cette semaine' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setTimeframe(tab.id)}
              className={`
                flex-1 py-2.5 px-4 rounded-xl text-sm font-medium transition-all
                ${timeframe === tab.id
                  ? 'bg-gradient-to-r from-[#FFE400] to-[#BDFF00] text-black'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </motion.div>

        {/* Leaderboard */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-[#FFE400] animate-spin" />
          </div>
        ) : leaderboard.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Aucun joueur pour le moment</p>
            <p className="text-gray-500 text-sm">Sois le premier à jouer !</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="space-y-2"
          >
            {leaderboard.map((player, index) => (
              <LeaderboardRow
                key={player.userId}
                player={player}
                index={index}
                isCurrentUser={currentUser?.uid === player.userId}
              />
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FanZoneLeaderboard;
